import React, { useEffect, useState, forwardRef, ForwardedRef } from "react";
import { Box, Button, Text, Icon, Menu, MenuButton, MenuList, MenuItem } from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";

export interface ClasswizeSelectOption<T> {
	value: T;
	label: string;
}

interface Props<T> {
	options: ClasswizeSelectOption<T>[];
	value?: T;
	onChange?: (value: T) => void;
	placeholder?: string;
	width?: string | number;
	isDisabled?: boolean;
	dataTestId?: string;
	/** When true, long labels ellipsize instead of overflowing the button. */
	truncateLabel?: boolean;
}

/**
 * A generic select component that can be used to select a value from a list of options.
 *
 * It supports a generic type T for the values, however due to a TypeScript limitation in explicit generic type params in JSX,
 * the onChange callback shows 'value' as 'unknown', so you must cast the value to the generic type in the onChange callback.
 *
 * @example
 * ```tsx
 * type Person = {
 *  name: string;
 *  age: number;
 * }
 *
 * const options: ClasswizeSelectOption<Person>[] = [
 *  { label: "Person 1", value: { name: "John", age: 20 } },
 *  { label: "Person 2", value: { name: "Jane", age: 21 } },
 * ];
 *
 * <ClasswizeSelect
 *  options={options}
 *  onChange={(value) => doSomethingWithPerson(value as Person)}
 *  placeholder="Select a person"
 * />
 * ```
 */
export const ClasswizeSelect = forwardRef(<T,>(props: Props<T>, ref: ForwardedRef<HTMLButtonElement>) => {
	const [selected, setSelected] = useState<T | undefined>(props.value);

	useEffect(() => {
		setSelected(props.value);
	}, [props.value]);

	const handleOptionSelect = (optionValue: T) => {
		setSelected(optionValue);
		props.onChange?.(optionValue);
	};

	const selectedOption = props.options.find((option) => option.value === selected);
	const displayText = selectedOption ? selectedOption.label : (props.placeholder ?? "Select");

	return (
		<Box width={props.width}>
			<Menu placement="bottom-start">
				{({ isOpen }) => (
					<>
						<MenuButton
							as={Button}
							width="100%"
							variant="outline"
							border="1px solid"
							borderColor="neutrals.50"
							_focus={{ boxShadow: "outline", borderColor: "brand.500" }}
							_hover={{ background: "neutrals.10" }}
							isDisabled={props.isDisabled}
							opacity={props.isDisabled ? 0.6 : 1}
							background="white"
							ref={ref}
							data-testid={props.dataTestId}
						>
							<Box display="flex" alignItems="center" justifyContent="space-between" minW={props.truncateLabel ? 0 : undefined}>
								<Text
									color={selected ? "black" : "gray.500"}
									fontWeight={400}
									textAlign="left"
									flex={props.truncateLabel ? 1 : undefined}
									minW={props.truncateLabel ? 0 : undefined}
									isTruncated={props.truncateLabel}
									title={props.truncateLabel ? displayText : undefined}
								>
									{displayText}
								</Text>
								<Icon
									as={ChevronDownIcon}
									color="gray.500"
									boxSize={6}
									ml={1}
									flexShrink={0}
									transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
									transition="transform 0.2s"
								/>
							</Box>
						</MenuButton>
						<MenuList
							border="1px solid"
							borderColor="gray.200"
							boxShadow="lg"
							maxH="300px"
							width={props.width}
							minWidth={props.width}
							overflowY="auto"
							px={2}
						>
							{props.options.map((option) => (
								<MenuItem
									key={String(option.value)}
									onClick={() => handleOptionSelect(option.value)}
									data-testid={`select-option-${String(option.value)}`}
									bg={selected === option.value ? "neutrals.30" : "white"}
									rounded={"md"}
									my={1}
									_hover={{ bg: "gray.100" }}
									_focus={{ bg: "neutrals.30" }}
									color="navy.900"
								>
									{option.label}
								</MenuItem>
							))}
						</MenuList>
					</>
				)}
			</Menu>
		</Box>
	);
});
